import { normalizeText } from './dataUtils';

// Appointment status options
export const APPOINTMENT_STATUS = {
    SCHEDULED: 'scheduled',
    IN_PROGRESS: 'in-progress',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled'
};

// Status display labels
export const STATUS_LABELS = {
    [APPOINTMENT_STATUS.SCHEDULED]: 'Programada',
    [APPOINTMENT_STATUS.IN_PROGRESS]: 'En curso',
    [APPOINTMENT_STATUS.COMPLETED]: 'Completada',
    [APPOINTMENT_STATUS.CANCELLED]: 'Cancelada'
};

// Validation functions
export const validateAppointmentForm = (formData, isExamForm = false) => {
    const errors = {};

    // For exam forms, skip the basic appointment validation since we're just updating medical details
    if (!isExamForm) {
        // Required fields validation for booking form
        if (!formData.dogId) {
            errors.dogId = 'Please select a dog';
        }

        if (!formData.appointmentDate) {
            errors.appointmentDate = 'Appointment date is required';
        }

        if (!formData.cause || formData.cause.trim().length === 0) {
            errors.cause = 'Reason for appointment is required';
        }

        // No past-date restriction — allows editing historical (completed/cancelled) appointments
    }

    // Temperature validation (Celsius)
    if (formData.temperature !== undefined && formData.temperature !== '') {
        const temp = parseFloat(formData.temperature);
        if (isNaN(temp) || temp < 32 || temp > 43) {
            errors.temperature = 'Temperature must be between 32-43°C';
        }
    }

    // Weight validation (kilograms)
    if (formData.weight !== undefined && formData.weight !== '') {
        const weight = parseFloat(formData.weight);
        if (isNaN(weight) || weight <= 0 || weight > 100) {
            errors.weight = 'Weight must be between 0.1-100 kg';
        }
    }

    // Follow-up validation
    if (formData.followUpRequired && !formData.followUpDate) {
        errors.followUpDate = 'Follow-up date is required when follow-up is needed';
    }

    if (formData.followUpDate && formData.appointmentDate) {
        const followUpDate = new Date(formData.followUpDate);
        const appointmentDate = new Date(formData.appointmentDate);
        
        if (followUpDate <= appointmentDate) {
            errors.followUpDate = 'Follow-up date must be after the appointment date';
        }
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Transform form data for API submission
export const transformAppointmentForSubmission = (formData, isExamForm = false) => {
    const transformed = {};

    if (!isExamForm && formData.dogId && formData.appointmentDate && formData.cause) {
        transformed.dogId = formData.dogId;
        transformed.appointmentDate = formData.appointmentDate;
        transformed.cause = formData.cause.trim();
    }

    if (formData.status) {
        transformed.status = formData.status;
    } else if (!isExamForm) {
        transformed.status = APPOINTMENT_STATUS.SCHEDULED;
    }

    // Add optional fields only if they have values
    if (formData.temperature !== undefined && formData.temperature !== '') {
        transformed.temperature = parseFloat(formData.temperature);
    }

    if (formData.weight !== undefined && formData.weight !== '') {
        transformed.weight = parseFloat(formData.weight);
    }

    if (formData.findings && formData.findings.trim()) {
        transformed.findings = formData.findings.trim();
    }

    if (formData.recommendations && formData.recommendations.trim()) {
        transformed.recommendations = formData.recommendations.trim();
    }

    if (formData.vetNotes && formData.vetNotes.trim()) {
        transformed.vetNotes = formData.vetNotes.trim();
    }

    transformed.followUpRequired = Boolean(formData.followUpRequired);

    if (formData.followUpRequired && formData.followUpDate) {
        transformed.followUpDate = formData.followUpDate;
    }

    return transformed;
};

// Format date for display
export const formatDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Format datetime for display
export const formatDateTime = (dateString) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';

        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        console.error('Error formatting datetime:', error, dateString);
        return 'Invalid date';
    }
};

// Format date for input field (YYYY-MM-DD)
export const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

// Sort appointments by date (most recent first)
export const sortAppointmentsByDate = (appointments) => {
    return [...appointments].sort((a, b) => {
        return new Date(b.appointmentDate) - new Date(a.appointmentDate);
    });
};

// Filter appointments by status
export const filterAppointmentsByStatus = (appointments, status) => {
    if (!status) return appointments;
    return appointments.filter(appointment => appointment.status === status);
};

// Search appointments by dog name or owner name
export const searchAppointments = (appointments, searchTerm) => {
    if (!searchTerm) return appointments;
    
    const term = normalizeText(searchTerm);
    return appointments.filter(appointment => {
        const dogName = normalizeText(appointment.dog?.name);
        const ownerNames = appointment.dog?.owners?.map(owner => normalizeText(owner.name)).join(' ') || '';
        const cause = normalizeText(appointment.cause);
        const walkInDog = normalizeText(appointment.walkInInfo?.dogName);
        const walkInOwner = normalizeText(appointment.walkInInfo?.fullName);

        return dogName.includes(term) || ownerNames.includes(term) || cause.includes(term)
            || walkInDog.includes(term) || walkInOwner.includes(term);
    });
};

// Find potential duplicate appointments
const findDuplicateAppointments = (appointments) => {
    const duplicates = [];
    const seen = new Map();
    
    appointments.forEach(appointment => {
        const key = `${appointment.dog?._id}-${appointment.cause}-${new Date(appointment.appointmentDate).toDateString()}`;
        
        if (seen.has(key)) {
            // Found a potential duplicate
            const original = seen.get(key);
            duplicates.push({
                original: original,
                duplicate: appointment,
                reason: 'Same dog, same reason, same day'
            });
        } else {
            seen.set(key, appointment);
        }
    });
    
    return duplicates;
};

// Log duplicate appointments for review
export const logDuplicateAppointments = (appointments) => {
    const duplicates = findDuplicateAppointments(appointments);
    
    if (duplicates.length > 0) {
        console.warn('🚨 Found potential duplicate appointments:');
        duplicates.forEach((dup, index) => {
            console.log(`Duplicate ${index + 1}:`);
            console.log('  Original:', {
                id: dup.original._id,
                dog: dup.original.dog?.name,
                date: dup.original.appointmentDate,
                cause: dup.original.cause
            });
            console.log('  Duplicate:', {
                id: dup.duplicate._id,
                dog: dup.duplicate.dog?.name,
                date: dup.duplicate.appointmentDate,
                cause: dup.duplicate.cause
            });
            console.log('  Reason:', dup.reason);
            console.log('---');
        });
    } else {
        console.log('✅ No duplicate appointments found');
    }
    
    return duplicates;
};
